import { useMutation, useQuery } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { useContext, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { Toast } from 'design-system/src/components/Toast/Toast';
import Button from 'design-system/src/components/Button/Button';
import ButtonRow, { Alignment } from 'design-system/src/components/ButtonRow/ButtonRow';
import { RadioLabel, RadioOptionWrapper, RadioBlockGroup } from 'design-system/src/components/Radio/Radio';
import { PageLoadError } from 'design-system/src/components/PageLoadError/PageLoadError';
import {
  client,
  NewCommentFile,
  FormFilesDocumentTypes,
  TaskComment,
  TaskEvent,
  OutboundTaskTypeKey,
  OutboundTaskType,
} from '../../../../api';
import type { Task, WithAdditionalTaskResponseFields, ResponseOption, ActionType } from '../../types';
import { TaskContext } from '../../contexts';
import { AuthContext } from '../../../../auth';
import { RenderHtmlString } from 'design-system/src/components/RenderHtmlString/RenderHtmlString';
import { ActionCard } from '../../../ActionCard';
import { AttachedFile } from '../../Files';
import DeclineModal from '../../DeclineModal';
import NewFollowupTaskModal from '../../NewFollowupTaskModal';
import AppealRequestModal, { AdditionalInput } from '../../AppealRequestModal';
import { TaskInstructionSummary, TaskCompletionEventBanner } from '../../shared';
import { OtherRequestButton } from '../../actions/shared/OtherRequestButton';
import { TaskActionsLoadingSkeleton } from '../../actions/TaskActionsLoadingSkeleton';
import {
  RadioLabelAndSubheader,
  RadioOptionWithSubheader,
  RadioOptionWithSubheaderWrapper,
} from '../../actions/shared/styles';
import { getBenefitTypeLabel } from '../../utils';
import { sponsoredTaskCopy } from '../../../businessRelationshipNotices/copy';
import { tasksQueryKey } from '../../../../queries';
import { useQueryClient } from '@tanstack/react-query';
import { StructuredTaskViewWrapper } from '../StructuredTaskViewWrapper';

type ModalType = 'decline' | 'initiate' | 'other_request';

/** Legacy mapping used when structured_pa_denial_tasks flag is off or action_type is missing */
const LEGACY_OPTION_TO_MODAL: Record<string, ModalType> = {
  initiate: 'initiate',
  cancel: 'decline',
};

/** Maps backend action_type to the modal that should be opened. "submit" is omitted — it submits directly without a modal. */
const ACTION_TYPE_TO_MODAL: Partial<Record<ActionType, ModalType>> = {
  confirm: 'initiate',
  cancel: 'decline',
  file_upload: 'initiate',
};

const fetchResponseOptions = async (denialId: string, edgeCaseType?: string | null): Promise<ResponseOption[]> => {
  const params: Record<string, string> = {};
  if (edgeCaseType) {
    params.edge_case_type = edgeCaseType;
  }
  const response = await client.get(`/response-options-for-denial/${denialId}`, { params });
  return response.data.options;
};

/**
 * Subcomponent that handles loading, error, and success states for dynamic next steps options.
 */
const DynamicNextStepsActionCard: React.FC<{
  denialId: string | null;
  optionsLoading: boolean;
  optionsError: boolean;
  options: ResponseOption[] | undefined;
  nextSteps: Array<{ label: string; subheader?: string; value: string; checked: boolean }>;
  setSelectedValue: (value: string) => void;
  setModalState: (state: { show: boolean; type: ModalType }) => void;
  handleContinue: () => void;
  isSubmitting: boolean;
  primaryButtonLabel: string;
}> = ({
  denialId,
  optionsLoading,
  optionsError,
  options,
  nextSteps,
  setSelectedValue,
  setModalState,
  handleContinue,
  isSubmitting,
  primaryButtonLabel,
}) => {
  if (optionsLoading) {
    return <TaskActionsLoadingSkeleton />;
  }

  // When denial_id is missing, query is disabled - show fallback instead of error
  if (!denialId) {
    return (
      <ActionCard>
        <div className="mb-4">
          <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
            Unable to load dynamic response options for this task. Please use the actions below.
          </Typography>
        </div>
        <ButtonRow align={Alignment.SpaceBetween} fullWidth>
          <ButtonRow align={Alignment.Left}>
            <Button variant="tertiary" size="sm" onClick={() => setModalState({ show: true, type: 'decline' })}>
              Cancel prescription
            </Button>
            <OtherRequestButton
              setShowNewFollowupTaskModal={(show) => setModalState({ show, type: 'other_request' })}
            />
          </ButtonRow>
        </ButtonRow>
      </ActionCard>
    );
  }

  if (optionsError || !options || options.length === 0) {
    return <PageLoadError />;
  }

  return (
    <ActionCard>
      <RadioBlockGroup $variant="outlined">
        {nextSteps.map((step) =>
          step.subheader ? (
            <RadioOptionWithSubheaderWrapper key={step.value}>
              <RadioOptionWrapper
                id={step.value}
                name="next-steps"
                value={step.value}
                checked={step.checked}
                onChange={() => setSelectedValue(step.value)}
                variant="outlined"
                disabled={isSubmitting}
              >
                <RadioOptionWithSubheader>
                  <RadioLabelAndSubheader>
                    <RadioLabel>{step.label}</RadioLabel>
                    <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
                      {step.subheader}
                    </Typography>
                  </RadioLabelAndSubheader>
                </RadioOptionWithSubheader>
              </RadioOptionWrapper>
            </RadioOptionWithSubheaderWrapper>
          ) : (
            <RadioOptionWrapper
              key={step.value}
              id={step.value}
              name="next-steps"
              value={step.value}
              checked={step.checked}
              onChange={() => setSelectedValue(step.value)}
              variant="outlined"
              disabled={isSubmitting}
            >
              <RadioLabel>{step.label}</RadioLabel>
            </RadioOptionWrapper>
          ),
        )}
      </RadioBlockGroup>
      <ButtonRow align={Alignment.SpaceBetween} fullWidth>
        <ButtonRow align={Alignment.Left}>
          <Button
            variant="tertiary"
            size="sm"
            onClick={() => setModalState({ show: true, type: 'decline' })}
            disabled={isSubmitting}
          >
            Cancel prescription
          </Button>
          <OtherRequestButton setShowNewFollowupTaskModal={(show) => setModalState({ show, type: 'other_request' })} />
        </ButtonRow>
        <Button variant="primary" size="sm" onClick={handleContinue} isLoading={isSubmitting}>
          {primaryButtonLabel}
        </Button>
      </ButtonRow>
    </ActionCard>
  );
};

/**
 * Dedicated task view for PA denied - Select next step (PA_DENIAL_NEXT_STEPS_DYNAMIC).
 * Options are loaded from the API per denial.
 */
const PaDenialNextStepsDynamicTaskView: React.FC<{
  taskResponse: WithAdditionalTaskResponseFields<Task>;
}> = ({ taskResponse }) => {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [modalState, setModalState] = useState<{ show: boolean; type: ModalType }>({
    show: false,
    type: 'decline',
  });
  const [newFilesUploaded, setNewFilesUploaded] = useState<NewCommentFile[]>([]);
  const [additionalInput, setAdditionalInput] = useState<AdditionalInput>({
    needs_draft_review: false,
  });
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState<string | null>(null);

  const { taskId, onTaskUpdate } = useContext(TaskContext);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { drug, patient, prescriber } = taskResponse;
  const task = taskResponse.task;
  const denialId = task.denial_id ?? null;
  const edgeCaseType = task.edge_case_type;
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const isCompleted = task.status === 'completed';
  const useActionType = authContext.user?.feature_flags?.structured_pa_denial_tasks === true;

  const {
    data: options,
    isLoading: optionsLoading,
    isError: optionsError,
  } = useQuery({
    queryKey: ['response-options-for-denial', denialId!, edgeCaseType],
    queryFn: () => fetchResponseOptions(denialId!, edgeCaseType),
    enabled: !!denialId && !isCompleted,
  });

  const nextSteps = useMemo(
    () =>
      (options ?? []).map((option, index) => ({
        label: option.label,
        subheader: option.subtitle,
        value: option.value,
        checked: selectedValue ? option.value === selectedValue : index === 0,
      })),
    [options, selectedValue],
  );

  const currentValue = selectedValue ?? options?.[0]?.value;
  const currentOption = options?.find((o) => o.value === currentValue);

  /** Resolve the modal type for the current option, or undefined if it should submit directly */
  const getModalTypeForOption = (option: ResponseOption | undefined): ModalType | undefined => {
    if (!option) return undefined;
    if (useActionType && option.action_type) {
      return ACTION_TYPE_TO_MODAL[option.action_type];
    }
    // Fallback: legacy value-based mapping
    return LEGACY_OPTION_TO_MODAL[option.value];
  };

  const currentOptionModalType = getModalTypeForOption(currentOption);
  const currentOptionHasModal = currentOptionModalType !== undefined;

  const primaryButtonLabel =
    currentOption?.value === 'initiate_program_enrollment'
      ? 'Initiate enrollment'
      : currentOptionHasModal
        ? 'Continue'
        : 'Submit';

  const confirmationText =
    edgeCaseType === 'program_enrollment'
      ? 'enrollment'
      : edgeCaseType === 'appeal_options'
        ? 'appeals'
        : 'appeals & enrollment';

  const closeModal = () => setModalState((prev) => ({ ...prev, show: false }));

  const handleContinue = () => {
    if (!currentValue) return;
    if (currentOptionModalType) {
      setModalState({ show: true, type: currentOptionModalType });
    } else {
      handleTaskResponse.mutate(currentValue);
    }
  };

  const handleTaskResponse = useMutation({
    mutationFn: async (response: string) => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking task response "${response}" for task ID: ${taskId}`);
        return;
      }

      const formData = new FormData();
      formData.append('task_response', response);
      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task response received.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const initiate = useMutation({
    mutationFn: async () => {
      if (isTrainingMode) {
        console.warn(`Training Mode: Mocking "initiate" for task ID: ${taskId}`);
        return;
      }

      const formData = new FormData();
      formData.append('task_response', currentValue ?? 'initiate');
      formData.append('message', inputRef.current?.value || '');
      formData.append('additional_input', JSON.stringify(additionalInput));

      if (newFilesUploaded.length) {
        const formFilesDocumentTypes: FormFilesDocumentTypes = {};
        newFilesUploaded.forEach((fileInfo) => {
          formData.append('files[]', fileInfo.file);
          formFilesDocumentTypes[fileInfo.file.name] = fileInfo.document_type;
        });
        formData.append('form_files_document_types', JSON.stringify(formFilesDocumentTypes));
      }

      await client.post(`tasks/${taskId}/respond`, formData);
    },
    onSuccess: () => {
      onTaskUpdate();
      toast.custom(() => <Toast variant="success">Task initiated.</Toast>);
      navigate('/tasks');
      queryClient.invalidateQueries({ queryKey: tasksQueryKey() });
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      console.error(error);
      Sentry.captureException(error);
    },
  });

  const extraData: { label: string; value: string }[] = [];
  const benefitTypeLabel = getBenefitTypeLabel(task);
  if (benefitTypeLabel) {
    extraData.push({ label: 'Benefit type', value: benefitTypeLabel });
  }

  const initialComment = task.timeline?.[0] as TaskComment | undefined;

  // Get completion event for completed tasks
  let completionEvent: TaskEvent | undefined;
  if (isCompleted) {
    if (task.timeline && task.timeline.length > 1) {
      const lastTimelineItem = task.timeline[task.timeline.length - 1];
      completionEvent = lastTimelineItem?.item_type === 'event' ? (lastTimelineItem as TaskEvent) : undefined;
    }
    if (!completionEvent) {
      Sentry.captureMessage('No completion event found for PA Denial Next Steps Dynamic task', {
        level: 'error',
        extra: {
          taskId,
        },
      });
    }
  }

  return (
    <>
      <StructuredTaskViewWrapper
        taskType={(task.task_title || task.type) as OutboundTaskType}
        generalInfoTableProps={{
          patientName: drug.patient_name,
          prescriberName: drug.prescriber_name,
          dob: drug.dob,
          prescribedOn: drug.prescription_recieved_at,
          drugName: drug.name,
          prescriptionId: drug.id,
          extraData,
        }}
        taskCreatedAt={task.created_at}
        isCompleted={isCompleted}
        showBusinessRelationshipSmallPrint
        sidebar={{
          drug: {
            data: drug,
            showBusinessRelationshipNotice: task.is_pharma_contract_task,
            businessRelationshipNoticeCopy: sponsoredTaskCopy,
          },
          patient: patient ?? undefined,
          prescriber: prescriber ?? undefined,
        }}
      >
        {/* Completion message for completed tasks */}
        {isCompleted && <TaskCompletionEventBanner completionEvent={completionEvent} />}

        {/* Instructions */}
        <div className={`mb-6 flex flex-col gap-4 ${isCompleted ? 'border-b-grayscale-100 pb-4 border-b-solid' : ''}`}>
          <TaskInstructionSummary>
            Please confirm if you'd like Tandem to continue with {confirmationText}:
          </TaskInstructionSummary>
          {initialComment && initialComment.item_type === 'comment' && (
            <>
              <Typography styledAs="bodyExtraSmallDMSans">
                <RenderHtmlString>{initialComment.message}</RenderHtmlString>
              </Typography>
              {initialComment.attachment_document_ids && initialComment.attachment_document_ids.length > 0 && (
                <div className="mt-2 flex flex-col gap-2">
                  {initialComment.attachment_document_ids.map((fileId) => (
                    <AttachedFile key={fileId} fileId={fileId} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Action card - only show when task is not completed */}
        {!isCompleted && (
          <DynamicNextStepsActionCard
            denialId={denialId}
            optionsLoading={optionsLoading}
            optionsError={optionsError}
            options={options}
            nextSteps={nextSteps}
            setSelectedValue={setSelectedValue}
            setModalState={setModalState}
            handleContinue={handleContinue}
            isSubmitting={handleTaskResponse.isPending}
            primaryButtonLabel={primaryButtonLabel}
          />
        )}
      </StructuredTaskViewWrapper>

      {modalState.show && modalState.type === 'decline' && (
        <DeclineModal
          closeModal={closeModal}
          patient={patient}
          drug={drug}
          taskKey={task.task_key as OutboundTaskTypeKey}
          taskId={taskId}
          onTaskUpdate={onTaskUpdate}
        />
      )}

      {modalState.show && modalState.type === 'initiate' && (
        <AppealRequestModal
          closeModal={closeModal}
          initiate={initiate}
          inputRef={inputRef}
          patient={patient}
          drug={drug}
          task={task}
          inputValue={inputValue}
          setInputValue={setInputValue}
          files={newFilesUploaded}
          onFilesChange={setNewFilesUploaded}
          setAdditionalInput={setAdditionalInput}
          additionalInput={additionalInput}
          appealRequestModalType="initiate_with_next_steps"
        />
      )}

      {modalState.show && modalState.type === 'other_request' && (
        <NewFollowupTaskModal
          closeModal={closeModal}
          patient={patient}
          drug={drug}
          patientId={patient.id}
          prescriptionId={drug.id}
          taskId={taskId}
          relatedTaskKey={task.task_key as OutboundTaskTypeKey}
          relatedTaskType={task.type as OutboundTaskType}
        />
      )}
    </>
  );
};

export default PaDenialNextStepsDynamicTaskView;
